import { Client } from "colyseus.js";
import { TABLE_CHAT_NOT_A_MEMBER } from "@bing/shared";
import type { DmConnector, DmMessage } from "./dm";
import { manageRoom, SeatExpiredError, type ConnectionRegistry } from "./lifecycle";
import { watchJoin } from "./join-watch";

/**
 * Is this the server saying the TABLE IS GONE (as opposed to any other join failure)?
 *
 * Table-chat membership is registered by the game room and cleared on its dispose, so this exact
 * refusal is terminal — retrying cannot bring a disposed table back. Everything else (a transport
 * drop, a DB blip behind «Unknown user») stays retryable.
 */
function isTableGone(error: unknown): boolean {
  return error instanceof Error && error.message.includes(TABLE_CHAT_NOT_A_MEMBER);
}

/**
 * A table chat message as broadcast by the game-server `chat` room — the server's
 * `ChatMessage` (which carries `senderName`), plus the voice discriminator. We map
 * it onto the shared {@link DmMessage} so the same store + UI render it.
 */
export interface TableChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  body: string;
  at: number;
  kind?: "text" | "voice";
  voiceId?: string;
  voiceDurationMs?: number;
}

function toDmMessage(m: TableChatMessage): DmMessage {
  return {
    id: m.id,
    senderId: m.senderId,
    // Kept so the shared message list can caption incoming bubbles — a table
    // thread has up to three peers, so lines must stay attributable.
    senderName: m.senderName,
    body: m.body,
    at: m.at,
    kind: m.kind ?? "text",
    voiceId: m.voiceId,
    voiceDurationMs: m.voiceDurationMs,
  };
}

/**
 * Table chat connector: joins the game-server `"chat"` Colyseus room correlated to
 * the current online table by `tableRoomId` — the table's OWN Colyseus roomId, which
 * every table-mate shares (from the lobby frame). The chat room groups joiners by it
 * (server-side `filterBy(["tableRoomId"])`), so only table-mates share the thread.
 * Table chat keeps no persisted history, so it opens empty. Returns the shared
 * {@link DmConnector} handle, so it drives `createChatStore` + the shared chat UI
 * exactly like a DM — only the transport differs.
 *
 * Lifecycle: managed like every other socket (probe on resume, backoff rejoin),
 * registered `silent` — the sheet shows its own inline notice, never the game
 * overlay. A rejoin is a plain fresh join and the chat room re-broadcasts NOTHING
 * historical, so the empty seed below wipes the store wholesale: after a reconnect
 * the panel is deliberately EMPTY-FRESH (like any new joiner) rather than showing
 * a thread with a silent hole where the dead-window messages would be.
 */
export function createColyseusTableChatConnector(opts: {
  wsUrl: string;
  tableRoomId: string;
  getInitData: () => string | null;
  /** Lifecycle registry (injected in tests; defaults to the app singleton). */
  registry?: ConnectionRegistry;
}): DmConnector {
  return async (handlers) => {
    const client = new Client(opts.wsUrl);
    const joinOptions = (): Record<string, unknown> => ({
      initData: opts.getInitData() ?? "",
      tableRoomId: opts.tableRoomId,
    });

    const managed = manageRoom({
      name: "chat",
      registry: opts.registry,
      silent: true,
      onStatus: (status) => handlers.onConnection?.(status),
      /*
       * ⚠️ THE INITIAL JOIN GETS THE SAME GUARD THE RE-ACQUIRE HAS HAD ALL ALONG. `isTableGone`
       * was applied only below, so a player whose table had already disposed was shown
       * «خطا در اتصال به گفتگو» WITH a «تلاش دوباره» that could never succeed — and an ERROR
       * beacon was filed for an ordinary, expected event. Measured on prod: 12 groups in 6 days
       * on the current bundle, every one of them `reason=other` carrying the raw English.
       * `SeatExpiredError` is what tells the store the refusal is TERMINAL (see
       * `ChatState.canRetry`); `joinFailureReason` classifies it as `table-gone` so the beacon
       * stops being unclassified noise.
       */
      initial: await watchJoin(
        { conn: "chat", kind: "chat" },
        client.joinOrCreate("chat", joinOptions()).catch((error: unknown) => {
          if (isTableGone(error)) throw new SeatExpiredError(String(error));
          throw error;
        }),
      ),
      // STOP when the table is gone. The lifecycle retried this refusal like any other failure —
      // for the full 135s budget, then filed an ERROR beacon — even though the table it is asking
      // for had been disposed and is never coming back. `SeatExpiredError` is the lifecycle's
      // terminal signal; on a `silent` connection it ends the cycle with no overlay and no beacon.
      reacquire: () =>
        client.joinOrCreate("chat", joinOptions()).catch((error: unknown) => {
          if (isTableGone(error)) throw new SeatExpiredError(String(error));
          throw error;
        }),
      wire: (room) => {
        // No history for table chat — the empty seed flips the store to "open" on
        // the first join and REPLACES the stale list on every rejoin (empty-fresh).
        handlers.onHistory([]);
        room.onMessage("say", (message: TableChatMessage) =>
          handlers.onMessage(toDmMessage(message)),
        );
        // Transport drop (no server message) → the lifecycle's silent reconnect
        // (onStatus) owns it; don't flip the thread to an error state. Only a real
        // server-sent message routes to the error path.
        room.onError((code: number, message?: string) => {
          if (message) handlers.onError(message);
        });
      },
    });

    return {
      say: (body) => managed.send("say", { body }),
      sayVoice: (voiceId, durationMs) => managed.send("say-voice", { voiceId, durationMs }),
      retry: () => managed.retry(),
      leave: () => managed.leave(),
    };
  };
}
